import { ReactNode, useMemo } from "react"
import { API_ENDPOINT } from "@mezo/sdk"
import { PassportProvider } from "@mezo-org/passport"
import { RainbowKitProvider } from "@rainbow-me/rainbowkit"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { WagmiProvider } from "wagmi"

import { MEZO_NETWORK, PASSPORT_API_URL } from "#/shared/constants"
import {
  FlowContext,
  useCreateFlowContext,
  useCreateWalletContext,
  WalletContext,
} from "#/shared/context"
import {
  bitcoinWallets,
  createWagmiConfig,
  ethereumWallets,
} from "#/shared/utils/rainbowKit"

const queryClient = new QueryClient()

// We need to create a provider here since we need to access
// Wagmi config before we call `useCreateWalletContext` hook
function WagmiWalletContextProvider({ children }: { children: ReactNode }) {
  const wallet = useCreateWalletContext()

  return (
    <WalletContext.Provider value={wallet}>{children}</WalletContext.Provider>
  )
}

export default function WalletInContextProvider({
  children,
}: {
  children: ReactNode
}) {
  const flow = useCreateFlowContext()

  const wagmiConfig = useMemo(
    () =>
      // TODO: The Wagmi config should be entirely sourced from Passport's
      // `getConfig` function bringing consistency between environments
      createWagmiConfig(flow.isBitcoinFlow ? bitcoinWallets : ethereumWallets),
    [flow.isBitcoinFlow],
  )

  return (
    <FlowContext.Provider value={flow}>
      <WagmiProvider config={wagmiConfig}>
        <QueryClientProvider client={queryClient}>
          <RainbowKitProvider>
            <PassportProvider
              environment={MEZO_NETWORK === "mezo" ? "mainnet" : "testnet"}
              authApiUrl={PASSPORT_API_URL}
              portalApiUrl={API_ENDPOINT}
            >
              <WagmiWalletContextProvider>
                {children}
              </WagmiWalletContextProvider>
            </PassportProvider>
          </RainbowKitProvider>
        </QueryClientProvider>
      </WagmiProvider>
    </FlowContext.Provider>
  )
}
